import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { db, auth } from '../firebase';
import { useAuthState } from 'react-firebase-hooks/auth';
import { QUESTIONNAIRE_SECTIONS } from '../lib/teacherQuestionnaire';
import { generateTermPlan } from '../lib/api';
import AppLayout from '../components/AppLayout';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import toast from 'react-hot-toast';

const TeachingPlanQuestionnairePage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [user] = useAuthState(auth);
  const [student, setStudent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [answers, setAnswers] = useState({});
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const selectedPath = searchParams.get('path');

  // Flatten all questions from all sections into a single array
  const allQuestions = QUESTIONNAIRE_SECTIONS.flatMap(section => 
    section.questions.map(question => ({
      ...question,
      sectionTitle: section.title,
      sectionId: section.id
    }))
  );

  // Scroll to top when question index changes
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [currentQuestionIndex]);

  useEffect(() => {
    const fetchStudent = async () => {
      try {
        setLoading(true);
        const studentRef = doc(db, 'students', id);
        const studentSnap = await getDoc(studentRef);
        
        if (studentSnap.exists()) {
          setStudent({ id: studentSnap.id, ...studentSnap.data() });
        } else {
          navigate('/students');
        }
      } catch (error) {
        console.error('Error fetching student:', error);
        toast.error('Error loading student data');
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchStudent();
    }
  }, [id, navigate]);

  const handleAnswerChange = (questionId, value) => {
    setAnswers(prev => ({
      ...prev,
      [questionId]: value
    }));
  };

  const handleSubmit = async () => {
    if (!user) {
      toast.error('Please log in to continue');
      return;
    }

    // Check if triage question is answered
    if (!answers.ncStatus) {
      toast.error('Please answer the curriculum placement question');
      return;
    }

    try {
      setSubmitting(true);
      
      // Save questionnaire answers first
      const studentRef = doc(db, 'students', id);
      await setDoc(studentRef, {
        questionnaireAnswers: answers,
        questionnaireCompletedAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      }, { merge: true });
      
      // Generate teaching plan with activity tiles
      const result = await generateTermPlan(id);

      toast.success('Teaching plan generated successfully!');
      
      // Navigate with the generated plan
      navigate(`/students/${id}/teaching-plan`, {
        state: { 
          plan: result,
          studentId: id,
          termPlanId: result.termPlanId,
          studentName: `${student?.firstName} ${student?.lastName}`
        }
      });
      
    } catch (error) {
      console.error('Error generating teaching plan:', error);
      toast.error(error.message || 'Failed to generate teaching plan');
    } finally {
      setSubmitting(false);
    }
  };

  const isCurrentQuestionValid = () => {
    const question = allQuestions[currentQuestionIndex];
    if (!question) return false;
    
    // Check if current question is answered (all questions are required)
    return answers[question.id] !== undefined;
  };

  const isFormComplete = () => {
    return allQuestions.every(question => answers[question.id] !== undefined);
  };

  const renderQuestion = (question) => {
    return (
      <div key={question.id} className="mb-6">
        <h3 className="text-lg font-medium text-gray-900 mb-3">
          {question.question}
        </h3>
        <div className="space-y-2">
          {question.options.map((option) => (
            <label key={option.value} className="flex items-start space-x-3 cursor-pointer">
              <input
                type="radio"
                name={question.id}
                value={option.value}
                checked={answers[question.id] === option.value}
                onChange={(e) => handleAnswerChange(question.id, e.target.value)}
                className="mt-1 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
              />
              <div className="flex-1">
                <span className="text-gray-900">{option.label}</span>
              </div>
            </label>
          ))}
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <AppLayout>
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading questionnaire...</p>
          </div>
        </div>
      </AppLayout>
    );
  }

  if (!student) {
    return (
      <AppLayout>
        <div className="text-center py-12">
          <h1 className="text-2xl font-bold text-gray-900">Student not found</h1>
          <p className="mt-2 text-gray-600">The requested student could not be found.</p>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div className="max-w-4xl mx-auto p-6">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">
            Teaching Plan Questionnaire
          </h1>
          <p className="mt-2 text-gray-600">
            Complete the questionnaire for {student.firstName} {student.lastName}
          </p>
          {selectedPath && (
            <p className="mt-1 text-sm text-blue-600">
              Selected path: {selectedPath}
            </p>
          )}
        </div>

        {/* Progress Indicator */}
        <div className="mb-6">
          <div className="flex justify-between text-sm text-gray-600 mb-2">
            <span>Question {currentQuestionIndex + 1} of {allQuestions.length}</span>
            <span>{Math.round(((currentQuestionIndex + 1) / allQuestions.length) * 100)}% Complete</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div 
              className="bg-indigo-600 h-2 rounded-full transition-all"
              style={{ width: `${((currentQuestionIndex + 1) / allQuestions.length) * 100}%` }}
            />
          </div>
        </div>

        {/* Current Question Content */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle>
              {allQuestions[currentQuestionIndex]?.sectionTitle}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {allQuestions[currentQuestionIndex] && (
              <div key={allQuestions[currentQuestionIndex].id}>
                {renderQuestion(allQuestions[currentQuestionIndex])}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Navigation Buttons */}
        <div className="flex justify-between items-center">
          <button
            onClick={() => navigate(`/students/${id}`)}
            className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
          >
            Cancel
          </button>
          
          <div className="flex space-x-3">
            <button
              onClick={() => setCurrentQuestionIndex(prev => Math.max(0, prev - 1))}
              disabled={currentQuestionIndex === 0}
              className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              Previous
            </button>
            
            {currentQuestionIndex < allQuestions.length - 1 ? (
              <button
                onClick={() => setCurrentQuestionIndex(prev => prev + 1)}
                disabled={!isCurrentQuestionValid()}
                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                Next
              </button>
            ) : (
              <button
                onClick={handleSubmit}
                disabled={submitting || !isFormComplete()}
                className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {submitting ? 'Generating Plan...' : 'Generate Teaching Plan'}
              </button>
            )}
          </div>
        </div>
      </div>
    </AppLayout>
  );
};

export default TeachingPlanQuestionnairePage;
